import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const INTERVAL_LABELS = { yearly: "jährlich", biyearly: "alle 2 Jahre", quarterly: "vierteljährlich", once: "einmalig" };

export default function SchulungPrint() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isTest = params.get("test") === "1";
  const [training, setTraining] = useState(null);
  const [center, setCenter] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/trainings/${id}`), api.get("/center")]).then(([t, c]) => {
      setTraining(t.data); setCenter(c.data);
    });
  }, [id]);

  if (!training || !center) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;
  }

  const today = new Date().toLocaleDateString("de-DE");

  return (
    <div className="min-h-screen bg-white">
      <div className="no-print sticky top-0 flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => window.close()}><ArrowLeft className="h-4 w-4 mr-1" /> Zurück</Button>
        <Button size="sm" onClick={() => window.print()} data-testid="btn-do-print"><Printer className="h-4 w-4 mr-1" /> Drucken</Button>
      </div>

      <div className="mx-auto max-w-3xl px-8 py-8 text-slate-900" data-testid="print-content">
        {/* Header */}
        <div className="flex items-center gap-4 border-b-2 border-teal-600 pb-4 mb-6">
          <img src="/haccp-logo.png" alt="HACCP" className="h-16 w-16 object-contain" />
          <div>
            <div className="font-heading text-xl font-bold">{center.name}</div>
            <div className="text-sm text-slate-600">{center.address} · Tel. {center.telephone}</div>
            <div className="text-xs text-slate-500">HACCP-Schulungsnachweis · Ruhetag: {center.rest_day}</div>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-bold mb-1">{isTest ? "Wissenstest: " : ""}{training.title}</h1>
        <p className="text-sm text-slate-600 mb-6">
          Kategorie: {training.category} · Turnus: {INTERVAL_LABELS[training.interval] || training.interval}
        </p>

        {!isTest ? (
          <>
            {training.summary && <p className="mb-5 text-[15px] leading-relaxed">{training.summary}</p>}
            {training.sections.map((s, i) => (
              <div key={i} className="mb-5">
                <h2 className="font-heading text-lg font-semibold text-teal-700 mb-2">{s.heading}</h2>
                <ul className="list-disc pl-6 space-y-1.5 text-[15px] leading-relaxed">
                  {(s.points || []).map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <>
            <p className="mb-5 text-[15px]">Bitte beantworten Sie die folgenden Fragen. Bestanden ab korrekt beantworteten Fragen nach Rücksprache.</p>
            <ol className="space-y-6 text-[15px]">
              {training.test_questions.map((q, i) => (
                <li key={i}>
                  <div className="font-semibold">{i + 1}. {q}</div>
                  <div className="mt-2 border-b border-dotted border-slate-400 h-6" />
                  <div className="mt-3 border-b border-dotted border-slate-400 h-6" />
                </li>
              ))}
            </ol>
          </>
        )}

        {/* Signature block */}
        <div className="mt-10 pt-6 border-t border-slate-300 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="border-b border-slate-400 h-8" />
            <div className="mt-1 text-slate-600">Name Teilnehmer</div>
          </div>
          <div>
            <div className="border-b border-slate-400 h-8" />
            <div className="mt-1 text-slate-600">Unterschrift Teilnehmer</div>
          </div>
          <div>
            <div className="border-b border-slate-400 h-8" />
            <div className="mt-1 text-slate-600">Datum ({today})</div>
          </div>
          <div>
            <div className="border-b border-slate-400 h-8" />
            <div className="mt-1 text-slate-600">Schulungsleitung</div>
          </div>
        </div>
      </div>
    </div>
  );
}
